import { memo, useCallback, useRef, type ReactNode } from 'react';
import { Pressable } from 'react-native';
import ReanimatedSwipeable, {
  type SwipeableMethods,
} from 'react-native-gesture-handler/ReanimatedSwipeable';
import { Icon, useTheme } from 'react-native-paper';

import { iconSize } from '@/theme';

export interface SwipeableRowAction {
  readonly label: string;
  readonly onPress: () => void;
}

interface SwipeableRowProps {
  readonly children: ReactNode;
  readonly rightAction: SwipeableRowAction;
}

const ACTION_WIDTH = 80;

const actionStyle = {
  width: ACTION_WIDTH,
  justifyContent: 'center' as const,
  alignItems: 'center' as const,
};

interface RightActionPanelProps {
  readonly label: string;
  readonly onPress: () => void;
}

const RightActionPanel = memo(({ label, onPress }: RightActionPanelProps) => {
  const theme = useTheme();
  return (
    <Pressable
      style={[actionStyle, { backgroundColor: theme.colors.error }]}
      onPress={onPress}
      accessibilityLabel={label}
      accessibilityRole="button">
      <Icon source="delete" size={iconSize.md} color={theme.colors.onError} />
    </Pressable>
  );
});

RightActionPanel.displayName = 'RightActionPanel';

const SwipeableRowBase = ({ children, rightAction }: SwipeableRowProps) => {
  const swipeableRef = useRef<SwipeableMethods | null>(null);

  const handleDelete = useCallback(() => {
    rightAction.onPress();
    swipeableRef.current?.close();
  }, [rightAction]);

  const renderRightActions = useCallback(
    () => <RightActionPanel label={rightAction.label} onPress={handleDelete} />,
    [rightAction.label, handleDelete],
  );

  return (
    <ReanimatedSwipeable
      ref={swipeableRef}
      renderRightActions={renderRightActions}
      overshootRight={false}
      rightThreshold={ACTION_WIDTH / 2}>
      {children}
    </ReanimatedSwipeable>
  );
};

export const SwipeableRow = memo(SwipeableRowBase);
